import React, {Component} from 'react';
import PropTypes from 'prop-types';
import deLocaleObject from 'rc-calendar/lib/locale/de_DE';
import enLocaleObject from 'rc-calendar/lib/locale/en_US';
import moment from 'moment';
import 'moment/locale/de';
import 'moment/locale/en-gb';
import {find, isArray} from 'lodash';
import classnames from 'classnames';

import 'rc-calendar/assets/index.css';
import bothCellBAckground from '../../Resources/Images/date_cell_both.svg';
import Calendar from 'rc-calendar';

const getLocaleObjectByName = (name) => {
    switch(name) {
        case 'de':
            return Object.assign(deLocaleObject, {monthFormat: 'MMMM'});
        case 'en':
            return Object.assign(enLocaleObject, {monthFormat: 'MMMM'});
        default:
            // console.warn('Missing calendar locale object for ' + name);
            return deLocaleObject;
    }
};

/**
 * This is a calendar view used for value date view or date picker in option forms.
 */
const calendarWidgetPropTypes = {
    // api params for `rc-calendar`
    className: PropTypes.string,
    showToday: PropTypes.bool,
    showDateInput: PropTypes.bool,
    locale: PropTypes.object,
    onChange: PropTypes.func,
    onSelect: PropTypes.func,
    disabledDate: PropTypes.func,
    timePicker: PropTypes.element,
    value: PropTypes.object,
    defaultValue: PropTypes.object,
    // a list of dates that should be highlighted in calendar
    highlights: PropTypes.arrayOf(
        PropTypes.shape({
            color: PropTypes.oneOf(['blue', 'green', 'base', 'counter', 'both']),
            date: PropTypes.instanceOf(moment)
        })
    ),
    fullBorders: PropTypes.bool,
    localeName: PropTypes.string.isRequired
};

class CalendarWidget extends Component {
    constructor(props) {
        super(props);
        this.calendarRef = React.createRef();
        this.state = {
            highlights: null
        };
    }

    componentWillReceiveProps(props){
        this.updateHighlights(props.highlights);
    };

     componentDidMount() {
        this.updateHighlights(this.props.highlights);
        this.observer = new MutationObserver((mutations) => {
            // Only run fixes if there were actual DOM changes
            if (mutations.length > 0) {
                this.runAccessibilityFixes();
            }
        });
 
        if (this.calendarRef.current) {
            this.observer.observe(this.calendarRef.current, {
                childList: true,
                subtree: true
            });
            this.runAccessibilityFixes(); // Run once on mount
        }
    }
    runAccessibilityFixes = () => {
    this.removeAriaRoles();
    this.fixRcCalendarRoles();
    this.fixInvalidTabIndex();
    this.fixCalendarAria();
    }

    componentWillUnmount() {
  if (this.observer) {
    this.observer.disconnect();
  }
}


    updateHighlights(highlights){
        if (highlights) {
            const newHighlights = isArray(highlights) ? highlights : [highlights];
            this.setState({highlights: newHighlights});
        } else {
            this.setState({highlights: null});
        }
    }

    fixCalendarAria = () => {
      document.querySelectorAll('.rc-calendar-date').forEach(el => {
        // ❌ Remove invalid aria-selected
        el.removeAttribute('aria-selected');
     
        // ✅ Add correct accessible label instead
        const dateText = el.innerText;
        if (dateText) {
          el.setAttribute('aria-label', `Select date ${dateText}`);
        }
      });
    };
    
    fixInvalidTabIndex = () => {
      // ❌ Remove tabindex from main calendar container
      document.querySelectorAll('.rc-calendar-calendar').forEach(el => {
        el.removeAttribute('tabindex');
      });
     
      // ❌ Remove from table wrapper if present
      document.querySelectorAll('.rc-calendar-table').forEach(el => {
        el.removeAttribute('tabindex');
      });
     
      // ❌ Remove from any non-clickable wrapper divs
      document.querySelectorAll('div[tabindex="0"]').forEach(el => {
        // Keep only actual date cells
        if (!el.classList.contains('rc-calendar-date')) {
          el.removeAttribute('tabindex');
        }
      });
    };
     
    removeAriaRoles = () => {
      document.querySelectorAll(`
        [role="grid"],
        [role="row"],
        [role="gridcell"],
        [role="table"],
        [role="columnheader"],
        [role="rowgroup"]
      `).forEach(el => {
        el.removeAttribute('role');
      });
    };
    
    fixMissingRoles = () => {
      document.querySelectorAll('[tabindex="0"]').forEach(el => {
        const tag = el.tagName.toLowerCase();
     
        // Skip native elements (already accessible)
        if (tag === 'button') return;
     
        // If it's <a> without href → needs role
        if (tag === 'a' && !el.getAttribute('href')) {
          el.setAttribute('role', 'button');
          return;
        }
     
        // If it's calendar date
        if (el.classList.contains('rc-calendar-date')) {
          el.setAttribute('role', 'button');
          return;
        }
     
        // If it's navigation or selectors
        if (
          el.className.includes('rc-calendar-prev') ||
          el.className.includes('rc-calendar-next') ||
          el.className.includes('rc-calendar-month') ||
          el.className.includes('rc-calendar-year')
        ) {
          el.setAttribute('role', 'button');
        }
      });
    };
    
    fixRcCalendarRoles = () => {
      // ❌ Remove wrong role from main container
      document.querySelectorAll('.rc-calendar-calendar').forEach(el => {
        el.removeAttribute('role');
      });
     
      // ❌ Also remove from table wrapper if exists
      document.querySelectorAll('.rc-calendar-table').forEach(el => {
        el.removeAttribute('role');
      });
     
      // ✅ Add role ONLY to actual date cells
      document.querySelectorAll('.rc-calendar-date').forEach(el => {
        el.setAttribute('role', 'button');
      });
     
      // ✅ Fix navigation buttons (already <a>, just ensure role)
      document.querySelectorAll(
        '.rc-calendar-prev-month-btn, .rc-calendar-next-month-btn, .rc-calendar-prev-year-btn, .rc-calendar-next-year-btn'
      ).forEach(el => {
        el.setAttribute('role', 'button');
      });
    };

    render(){
        const props = this.props;
        const classNames = classnames('callendar', props.className, {
            'callendar--fullBorders': props.fullBorders
        });

        moment.locale(props.localeName === 'en' ? 'en-gb' : 'de');

        let highlightsMap = {};
        if (this.state.highlights) {
            this.state.highlights.forEach(h => {
                if (h.date) {
                    highlightsMap[h.date.format('YYYY-MM-DD')] = h.color;
                }
            });
        }

        const customDateRender = (current) => {
            const currentStr = current.format('YYYY-MM-DD');
            const color = highlightsMap[currentStr] || null;
            
            let style = {};
            let classStr = "rc-calendar-date";
            if (color === 'both') {
                style = { backgroundImage: `url(${bothCellBAckground})`, color: '#fff' };
                classStr += ' rc-calendar-date-both';
            } else if (color) {
                classStr += ` rc-calendar-date-${color}`;
            }
            return (
                <div className={classStr} style={style}>{current.date()}</div>
            );
        };

        if(!this.state.highlights) {
            return (
                <div ref={this.calendarRef}>
                    <Calendar
                        showToday={false}
                        className={classNames}
                        showDateInput={false}
                        locale={getLocaleObjectByName(props.localeName)}
                        onChange={props.onChange}
                        onSelect={props.onSelect}
                        disabledDate={props.disabledDate}
                        dateRender={customDateRender}
                    />
                </div>
            )
        }

        return (
            <div ref={this.calendarRef}>
                <Calendar
                    className={classNames}
                    showToday={false}
                    showDateInput={false}
                    disabledDate={() => true}
                    locale={getLocaleObjectByName(props.localeName)}
                    dateRender={customDateRender}
                    onChange={() => {
                        window.setTimeout(() => {
                            if (this.calendarRef.current) this.runAccessibilityFixes();
                        }, 2);
                }}/>
            </div>
        );
    };
}

CalendarWidget.propTypes = calendarWidgetPropTypes;

const DatePicker = (props) => {
    //console.log("Calendar.js - ",props.locale);
    return (
        <div className="date__picker" onClick={props.onClick}>
            {props.value ? props.value.format(props.locale === 'de' ? 'DD.MM.YYYY' : 'YYYY-MM-DD') : ""}
        </div>
    );
};

export default CalendarWidget;
export {DatePicker};
